import { z } from 'zod'

/**
 * What an API token is allowed to touch. Mirrors the `ApiTokenScope` enum in the
 * Prisma schema — the two must stay in sync.
 *
 * - `EVENTS` — events, plans, phases, documents, rentals, brought-in items, the calendar.
 * - `VENUES` — venues, spaces, versions, tracks, saved layouts, inventory, floor-plan import.
 * - `PEOPLE` — users, clients, invitations, roles, and venue managers.
 * - `VENDOR_SELF` — a vendor's own profile, services, packages and furniture library, and the
 *   quote requests and reviews it receives. Vendors only.
 * - `IMPORTS` — Prism import jobs and the catalog of events to import.
 * - `FURNITURE` — the tenant's furniture catalog and saved arrangements.
 * - `VENDOR_APPROVALS` — which vendors may serve your venues, and what for.
 * - `UPLOADS` — uploading an image, so a caller that sets a cover or avatar can.
 * - `ADS` — reading the ad numbers: each venue's, and the company's table.
 * - `LEAD_STATS` — reading the lead stats and the pipeline: each venue's, the
 *   company's, and the couples behind a venue's count. Read only: connecting Google Ads and pointing accounts at venues are never
 *   done with a token.
 *
 * Venue managers sit under `PEOPLE` rather than `VENUES` because assigning a venue
 * to somebody grants them authority over it. That is people management wearing a
 * venue's clothes, and a token that may reshape a floor plan should not thereby be
 * able to hand the venue to someone.
 *
 * A scope narrows, it never grants: what a token may do is its owner's role
 * intersected with its scopes, so `PEOPLE` on a venue admin's token still cannot
 * invite an organization admin. Without that rule scopes would be a second,
 * competing permission system.
 *
 * Read versus write is not in here. It is a flag on each grant, and it follows the
 * HTTP method — `GET` reads, everything else writes — the same way the token-wide
 * `readOnly` flag already behaves.
 *
 * See `docs/api-token-scopes.md` for how the nine were drawn and which routes
 * fall under each.
 */
export const API_TOKEN_SCOPES = [
  'EVENTS',
  'VENUES',
  'PEOPLE',
  'VENDOR_SELF',
  'IMPORTS',
  'FURNITURE',
  'VENDOR_APPROVALS',
  'UPLOADS',
  'ADS',
  'LEAD_STATS',
] as const

export const apiTokenScopeSchema = z.enum(API_TOKEN_SCOPES)

export type ApiTokenScope = z.infer<typeof apiTokenScopeSchema>

/**
 * Scopes to present as consequential rather than list as equals.
 *
 * `PEOPLE` creates accounts, sends mail from your domain and changes roles, so a
 * token holding it can become a durable human foothold that outlives revoking the
 * token. `IMPORTS` drives somebody else's product using a stored session.
 *
 * Not a permission check — the guard treats every scope alike. This is for the
 * screen that hands them out, so choosing one of these for a token that reads a
 * calendar takes a deliberate act.
 */
export const HIGH_RISK_API_TOKEN_SCOPES: readonly ApiTokenScope[] = ['PEOPLE', 'IMPORTS']

/**
 * Scopes whose routes only read. Write on one of them would grant nothing, so
 * the screen that hands them out does not offer it, and does not send it.
 */
export const READ_ONLY_API_TOKEN_SCOPES: readonly ApiTokenScope[] = ['ADS', 'LEAD_STATS']
